import { useQuery } from "@tanstack/react-query";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Header from "@/components/header";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";

interface FAQItem {
  id: number;
  question: string;
  videoId: string;
  sortOrder: number;
}

export default function FAQPage() {
  const { t } = useTranslation();
  
  const { data: faqItems, isLoading } = useQuery<FAQItem[]>({
    queryKey: ['/api/faq'],
    queryFn: async () => {
      const response = await fetch('/api/faq');
      if (!response.ok) {
        throw new Error('Failed to fetch FAQ items');
      }
      return response.json();
    },
  });

  // Sort FAQ items by sortOrder
  const sortedFaqItems = faqItems?.sort((a, b) => a.sortOrder - b.sortOrder);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="h-12 w-12 text-orange-500 animate-spin mb-4" />
          <p className="text-gray-400">{t('other.faq_page.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <Header />
      <div className="container mx-auto pt-24 px-4 md:px-6 pb-16 max-w-4xl">
        <div className="text-center mb-10">
          <div className="inline-block p-1.5 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 mb-4">
            <div className="bg-black rounded-full p-2">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#F7931A" strokeWidth="2" />
                <path d="M15.93 11.36C16.01 10.35 15.24 9.77 14.2 9.39L14.56 7.95L13.69 7.74L13.34 9.13C13.1 9.08 12.85 9.03 12.61 8.98L12.95 7.58L12.08 7.37L11.72 8.81C11.53 8.77 11.34 8.73 11.16 8.69L11.16 8.68L9.97 8.4L9.75 9.33C9.75 9.33 10.38 9.47 10.37 9.48C10.76 9.57 10.85 9.83 10.83 10.03L10.42 11.67C10.45 11.68 10.5 11.69 10.55 11.71L10.42 11.68L9.85 13.95C9.79 14.06 9.65 14.23 9.38 14.16C9.39 14.18 8.77 14.01 8.77 14.01L8.36 15L9.48 15.27C9.71 15.33 9.92 15.38 10.14 15.44L9.77 16.9L10.64 17.11L11 15.67C11.25 15.73 11.49 15.78 11.72 15.83L11.36 17.26L12.23 17.47L12.6 16.02C14.05 16.28 15.11 16.18 15.59 14.86C15.97 13.82 15.61 13.23 14.86 12.85C15.42 12.71 15.85 12.33 15.93 11.36ZM13.48 14.17C13.21 15.21 11.43 14.67 10.86 14.53L11.34 12.61C11.91 12.75 13.77 13.08 13.48 14.17ZM13.76 11.33C13.51 12.28 12.03 11.82 11.56 11.7L11.99 9.97C12.47 10.09 14.03 10.34 13.76 11.33Z" fill="#F7931A" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-yellow-400">
            {t('other.faq_page.title')}
          </h1>
          <p className="text-gray-400 max-w-lg mx-auto">
            {t('other.faq_page.subtitle')}
          </p>
        </div>

        <Accordion 
          type="single" 
          collapsible 
          className="space-y-4"
        >
          {sortedFaqItems?.map((item) => (
            <AccordionItem 
              key={item.id} 
              value={item.id.toString()}
              className="border border-gray-800 bg-gray-900/40 backdrop-blur-sm rounded-lg overflow-hidden transition-all hover:border-orange-800/60"
            >
              <AccordionTrigger className="text-lg font-medium px-6 py-4 text-left text-white hover:text-orange-400 transition-colors">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 pt-2">
                <div className="rounded-lg overflow-hidden shadow-xl border border-gray-800">
                  <div className="aspect-w-16 aspect-h-9">
                    <iframe
                      src={`https://www.youtube.com/embed/${item.videoId}`}
                      title={item.question}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}